import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from '@material-ui/core';
import get from 'lodash.get';
import LoadingButton from '~/modules/common/components/LoadingButton';
import {
  TABLE_KEY,
  getParsedDialogColumns,
  getTableSettingSmartMatchDropdownOptions,
  getAssignmentDialogColumnOptions,
  buildTableSettingDialogDefaultColumnConfiguration
} from '~/modules/resourcing/components/ResourceAssignmentDialog/enhancers';
import { usePutTableSettings } from '~/modules/common/components/ListTable';
import { AssignmentDialogColumns } from '~/modules/resourcing/components/ResourceAssignmentDialog/enhancers/constants';
import { useResourceAssignmentDialogContext } from '~/modules/resourcing/components/ResourceAssignmentDialog/ResourceAssignmentDialogContext';
import useDialogSettings from '~/modules/common/hooks/useDialogSettings';
import { useTagKeys } from '~/modules/tags/components/Dropdowns/TagKeyDropdown';
import { useAssignmentDialogColumnPermission } from '~/modules/resourcing/components/ResourceAssignmentDialog/hooks';
import { useGetEnabledCustomFieldsQuery } from '~/modules/customFields/graphql';
import useSelectedOptions from '../../hooks/useSelectedOptions';
import useDialogSettingsSave from '../../hooks/useDialogSettingsSave';
import ColumnSettingsDialogContent from './ColumnSettingsDialogContent';

const useStyles = makeStyles(theme => ({
  actionsSpacer: {
    flexGrow: 1,
    flexShrink: 1
  },
  content: {
    flexGrow: 1,
    minHeight: 200,
    minWidth: 280
  },
  spinner: {
    position: 'absolute',
    left: '50%',
    bottom: '50%'
  }
}));

const useColumnData = (me, isPsaPraaResourceMatchingEnabled) => {
  const columnPermissions = useAssignmentDialogColumnPermission();

  const {
    data: customFieldsData,
    loading: isCustomFieldsLoading
  } = useGetEnabledCustomFieldsQuery({
    variables: { groupName: 'User' }
  });

  const { data: tagOptions, loading: isTagsLoading } = useTagKeys({
    objectBinding: 'RESOURCE',
    search: ''
  });

  const customFieldDefinitions =
    get(customFieldsData, 'enabledCustomFields') || [];

  const smartMatchOptions = getTableSettingSmartMatchDropdownOptions({
    me,
    includeResourceSmartMatchColumn: isPsaPraaResourceMatchingEnabled
  });

  const allDropdownOptions = getAssignmentDialogColumnOptions({
    me,
    columnPermissions,
    customFieldDefinitions,
    tagFieldData: tagOptions
  });

  const availableDropdownOptions = allDropdownOptions.filter(
    option => option.field !== AssignmentDialogColumns.DisplayText
  );

  return {
    smartMatchOptions,
    availableDropdownOptions,
    allDropdownOptions,
    isLoading: isCustomFieldsLoading || isTagsLoading
  };
};

const ColumnSettingsDialog = ({ open, onClose, me }) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);

  const {
    selectedDialogOptions,
    setSelectedDialogOptions,
    setEnabledAssignmentDialogColumns,
    setIsCostOptimizationSmartMatchModeEnabled,
    isSmartMatchTabSelected
  } = useResourceAssignmentDialogContext();

  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = me;

  const defaultColumnConfiguration = buildTableSettingDialogDefaultColumnConfiguration(
    {
      me,
      includeResourceSmartMatchColumn: isPsaPraaResourceMatchingEnabled
    }
  );

  const {
    data: initialDialogState,
    isLoading: isDialogSettingsLoading
  } = useDialogSettings({
    tableKey: TABLE_KEY,
    defaultColumns: defaultColumnConfiguration
  });

  const {
    smartMatchOptions,
    availableDropdownOptions,
    allDropdownOptions,
    isLoading: isColumnDataLoading
  } = useColumnData(me, isPsaPraaResourceMatchingEnabled);

  const { onOptionChange, resetSelection, resetToDefault } = useSelectedOptions(
    {
      initialState: initialDialogState,
      defaultColumns: defaultColumnConfiguration,
      allDropdownOptions: [...smartMatchOptions, ...allDropdownOptions],
      selectedOptions: selectedDialogOptions,
      setSelectedOptions: setSelectedDialogOptions
    }
  );

  const handleCancel = useCallback(() => {
    onClose();
    resetSelection();
  }, [resetSelection, onClose]);

  const [putTableSettings] = usePutTableSettings();
  const handleSave = useDialogSettingsSave({
    putTableSettings,
    setIsSaving,
    onClose,
    selectedOptions: selectedDialogOptions,
    tableKey: TABLE_KEY,
    setEnabledAssignmentDialogColumns,
    setIsCostOptimizationSmartMatchModeEnabled,
    isPsaPraaResourceMatchingEnabled,
    isSmartMatchTabSelected
  });

  const isLoading = isDialogSettingsLoading || isColumnDataLoading;

  useEffect(() => {
    if (!isLoading && initialDialogState) {
      const columnKeys = initialDialogState.values();
      const initialSelectedOptions = getParsedDialogColumns({
        columns: columnKeys,
        allDropdownOptions: [...smartMatchOptions, ...allDropdownOptions]
      });

      setSelectedDialogOptions(initialSelectedOptions);
    }
  }, [isLoading]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      aria-labelledby="resource-allocation-settings-dialog-title"
      aria-describedby="resource-allocation-settings-dialog-description"
    >
      <DialogTitle id="resource-allocation-dialog-title">
        <FormattedMessage id="columnSettings.tableSettings" />
      </DialogTitle>
      <DialogContent className={classes.content}>
        {!isLoading && selectedDialogOptions.length !== 0 ? (
          <ColumnSettingsDialogContent
            isPsaPraaResourceMatchingEnabled={isPsaPraaResourceMatchingEnabled}
            isSmartMatchTabSelected={isSmartMatchTabSelected}
            dropdownFilteredOptions={availableDropdownOptions}
            selectedDialogOptions={selectedDialogOptions}
            smartMatchDropdownOptions={smartMatchOptions}
            onOptionChange={onOptionChange}
          />
        ) : (
          <CircularProgress size={20} className={classes.spinner} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetToDefault}>
          <FormattedMessage id="button.resetToDefault" />
        </Button>
        <span className={classes.actionsSpacer} />
        <Button onClick={handleCancel}>
          <FormattedMessage id="button.cancel" />
        </Button>
        <LoadingButton
          isLoading={isSaving}
          onClick={handleSave}
          color="primary"
          autoFocus
          disabled={isLoading}
        >
          <FormattedMessage id="button.save" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

ColumnSettingsDialog.propTypes = {
  me: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ColumnSettingsDialog;
